import { HelpCircle, Info, ListChecks, Sparkles } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { getPageDescription } from "@/lib/page-descriptions";
import { useLocation } from "react-router-dom";
import { useUiLanguage } from "@/lib/use-ui-language";

const LABELS = {
  ar: {
    tooltip: "شرح الصفحة",
    functions: "وظائف الصفحة",
    steps: "خطوات الاستخدام",
  },
  en: {
    tooltip: "Page guide",
    functions: "Page features",
    steps: "How to use",
  },
} as const;

const PageExplainer = () => {
  const { pathname } = useLocation();
  const lang = useUiLanguage();
  const desc = getPageDescription(pathname, lang);
  const t = LABELS[lang];

  if (!desc) return null;

  const dir = lang === "ar" ? "rtl" : "ltr";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-primary"
          title={t.tooltip}
          aria-label={t.tooltip}
        >
          <Info className="w-5 h-5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-96 max-h-[80vh] overflow-y-auto p-0"
        dir={dir}
      >
        <div className="bg-primary/5 border-b border-border px-4 py-3 sticky top-0 backdrop-blur z-10">
          <h4 className="font-bold text-sm text-foreground flex items-center gap-2">
            <HelpCircle className="w-4 h-4 text-primary" />
            {desc.title}
          </h4>
        </div>

        <div className="px-4 pt-4">
          <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-primary">
            <Sparkles className="w-3.5 h-3.5" />
            <span>{t.functions}</span>
          </div>
          <ul className="space-y-2">
            {desc.points.map((point, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-sm text-muted-foreground"
              >
                <span className="text-primary mt-0.5 shrink-0">•</span>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>

        {desc.steps && desc.steps.length > 0 && (
          <div className="px-4 py-4 mt-2 border-t border-border bg-muted/30">
            <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
              <ListChecks className="w-3.5 h-3.5" />
              <span>{t.steps}</span>
            </div>
            <ol className="space-y-2">
              {desc.steps.map((step, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-sm text-foreground/80"
                >
                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default PageExplainer;
